import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AGUIconName } from '../icon/icon-paths';

/** The panel's tone: the blue info disc, the error disc, or the lapsed-membership lock disc (P/proto-states.js:147-148). */
export type AGUStatePanelVariant = 'panel' | 'error' | 'lock';

/**
 * One product-state panel, `.ps-panel` (P/proto-states.js:47-49, 144-154): an icon disc, a heading, one sentence, up to two
 * buttons (the second always the quiet `.q` one), an optional foot line and the retry note. It takes the place of a list or a
 * page body — empty results, a failed load, a listing that is gone — and spans the grid it sits in (`grid-column: 1/-1`).
 *
 * The caller owns the copy and what each button does: the panel only emits `Primary`/`Secondary`. A secondary with an href
 * (the Contact support `mailto:`) renders as a real link; the caller omits it by passing a null label.
 */
@Component({
    standalone: false,
    selector: 'agu-state-panel',
    templateUrl: './state-panel.component.html',
    styleUrl: './state-panel.component.scss',
})
export class AGUStatePanelComponent {
    @Input() Variant: AGUStatePanelVariant = 'panel';
    @Input() Icon: AGUIconName = 'inbox';
    @Input() Title = '';
    @Input() Body = '';
    @Input() PrimaryLabel: string | null = null;
    @Input() SecondaryLabel: string | null = null;
    /** When set, the secondary is an `<a>` to this address instead of a button. */
    @Input() SecondaryHref: string | null = null;
    /** The small line under the buttons — the error's "Error {status} · ref {id}" (P/proto-states.js:153). */
    @Input() Foot: string | null = null;
    /** The retry note after a second failure (P/proto-states.js:154, 234-237). */
    @Input() Note: string | null = null;

    @Output() readonly Primary = new EventEmitter<void>();
    @Output() readonly Secondary = new EventEmitter<void>();

    OnPrimary(): void {
        this.Primary.emit();
    }

    OnSecondary(): void {
        this.Secondary.emit();
    }
}
